/**
 * Mini-map: draws OSM tile imagery on a <canvas> with the car position overlaid.
 *
 * - Tile source: OpenStreetMap standard layer (zoom 15)
 * - Canvas always shows the car at the centre; tiles shift as the car moves.
 * - The map is north-up; the car triangle rotates to show the heading.
 * - Tiles are cached by key so each tile is downloaded at most once per session.
 */

const ZOOM       = 15;
const TILE_PX    = 256;   // OSM tile size in pixels
// How many metres to show on each side of the canvas centre
const VIEW_HALF  = 600;   // metres

export class Minimap {
  /**
   * @param {HTMLCanvasElement} canvas
   */
  constructor(canvas) {
    this._canvas    = canvas;
    this._ctx       = canvas.getContext('2d');
    this._W         = canvas.width;
    this._cache     = new Map(); // key → HTMLImageElement | 'loading' | 'error'
    this._proj      = null;
    this._centerLat = 0;
    this._centerLng = 0;
  }

  /** Call once per loadArea() with the new center coords. */
  setCenter(lat, lng /*, radiusM – unused */) {
    this._centerLat = lat;
    this._centerLng = lng;
    this._prefetchTiles(lat, lng);
  }

  /** Call once per frame from the render loop. */
  draw(carState, proj) {
    if (!proj) return;
    this._proj = proj;

    // Unproject car scene position to geographic coords
    const { lat, lon: lng } = proj.unproject(carState.x, carState.z);

    // Prefetch tiles around the car (cheap if already cached)
    this._prefetchTiles(lat, lng);

    const ctx = this._ctx;
    const W   = this._W;

    // ── metres-per-pixel for this latitude ─────────────────────────────────
    // mpp = 156543.03392 * cos(lat) / 2^zoom
    const mpp = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, ZOOM);
    // How many OSM tile pixels fit in VIEW_HALF metres:
    //   tilePixels = VIEW_HALF / mpp
    // We want those tilePixels to fill W/2 canvas pixels:
    //   scale = (W/2) / (VIEW_HALF / mpp)
    const scale = (W / 2) * mpp / VIEW_HALF; // canvas_px per tile_px

    // Fractional tile coordinate of the car
    const n    = Math.pow(2, ZOOM);
    const carTX = (lng + 180) / 360 * n;
    const latR  = lat * Math.PI / 180;
    const carTY = (1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2 * n;

    ctx.clearRect(0, 0, W, W);

    // ── Draw tiles (rotated so car heading always faces canvas top) ──────────
    // angle=0 → facing south; rotating map by (angle−π) makes heading point up.
    const tileSize = TILE_PX * scale; // size of one tile on canvas in px

    ctx.save();
    ctx.translate(W / 2, W / 2);
    ctx.rotate(carState.angle - Math.PI);
    for (let dy = -3; dy <= 3; dy++) {
      for (let dx = -3; dx <= 3; dx++) {
        const tx = Math.floor(carTX) + dx;
        const ty = Math.floor(carTY) + dy;
        const key = `${ZOOM}/${tx}/${ty}`;
        const img = this._cache.get(key);
        if (!img || img === 'loading' || img === 'error') continue;

        // Tile offset from car (car is at origin after translate)
        const px = (tx - carTX) * tileSize;
        const py = (ty - carTY) * tileSize;
        ctx.drawImage(img, px, py, tileSize, tileSize);
      }
    }
    ctx.restore();

    // ── Car triangle — always at centre pointing up (heading direction) ─────
    ctx.save();
    ctx.translate(W / 2, W / 2);
    ctx.fillStyle   = '#ff3333';
    ctx.strokeStyle = 'white';
    ctx.lineWidth   = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, -8);   // nose (up = heading)
    ctx.lineTo(-5,  5);
    ctx.lineTo( 5,  5);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // ── Circular clip & border ───────────────────────────────────────────────
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(W / 2, W / 2, W / 2 - 1, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.restore();
  }

  // ── Tile loading ──────────────────────────────────────────────────────────

  _prefetchTiles(lat, lng) {
    const n  = Math.pow(2, ZOOM);
    const tx = Math.floor((lng + 180) / 360 * n);
    const latR = lat * Math.PI / 180;
    const ty = Math.floor((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2 * n);

    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        this._loadTile(tx + dx, ty + dy);
      }
    }
  }

  _loadTile(tx, ty) {
    const key = `${ZOOM}/${tx}/${ty}`;
    if (this._cache.has(key)) return;
    this._cache.set(key, 'loading');

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => this._cache.set(key, img);
    img.onerror = () => this._cache.set(key, 'error');
    img.src     = `https://tile.openstreetmap.org/${ZOOM}/${tx}/${ty}.png`;
  }
}
